// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § Providing and sharing, § API contract (crew)
// ==============================================
// /papers/requests/:rid/items/:iid — one requested paper (Crew-4).
//
//   • a file: take a photo or choose a file. Images become an upright JPEG of at
//     most 2500 px on the phone (imageNormalise.ts) before upload; PDFs go as
//     they are. Upload progress shows; every refusal (413, 415, …) is shown here.
//   • papers that expire get an expiry date; the passport also its details
//     (read from the photo on the phone when the reader is on — passportReaderUi.tsx).
//   • "Use the one already in my papers" makes a grant-only copy of it.
//   • "Not needed", with a reason, where the item allows it.
//   • the emergency contact is a short form, not a file.
// Answering only files the paper in the crew member's own papers; the skipper
// sees nothing until Share.
// ==============================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../services/api';
import { useCrewRequest, useSetCrewRequest } from '../hooks';
import { apiError } from '../apiError';
import { normaliseForUpload, NormaliseError } from '../imageNormalise';
import { isAnswered, isSharedNow, isWritable, itemExpires, itemHint, itemStatus } from '../itemDisplay';
import { EMPTY_PASSPORT, passportFromDetails, passportPayload } from '../passport';
import { PassportReadBadge, PassportReadNote, passportFieldsLabel, usePassportReader } from '../passportReaderUi';
import type { PassportDetails } from '../passport';
import { accessEndDate, fmtDay, firstName } from '../dates';
import type { CrewItemView, CrewRequestView, MyPaper } from '../types';
import { C, CrewShell, ErrorNote, Loading, PrimaryButton, SecondaryButton, StepHeading } from '../ui';
import { ItemQuestion, ValidityLine } from '../ValidityLine';

const fmtSize = (b: number) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

function Field({ label, children, error, id }: { label: string; children: React.ReactNode; error?: string | null; id: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-semibold text-[#c7d3e6]">{label}</label>
      {children}
      {error && <span id={`${id}-err`} className="text-sm text-[#ffb3b3]">{error}</span>}
    </div>
  );
}

/** Where to go after answering: Share once everything is answered, else the checklist. */
function afterAnswer(v: CrewRequestView, rid: string) {
  return v.items.every(isAnswered) && v.items.some((i) => i.paper && !isSharedNow(i)) ? `/papers/requests/${rid}/share` : `/papers/requests/${rid}`;
}

// ---------------- a file (photo or PDF) ----------------

function FileAnswer({ rid, item, onDone, prefill }: { rid: string; item: CrewItemView; onDone: (v: CrewRequestView) => void; prefill: Record<string, string | null> | null }) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [expires, setExpires] = useState(item.paper?.expiresOn ?? '');
  const [pp, setPp] = useState<PassportDetails>(EMPTY_PASSPORT);
  const isPassport = item.type === 'passport';
  const saveRef = useRef<HTMLButtonElement>(null);
  const reader = usePassportReader(isPassport);

  useEffect(() => { if (prefill) { const p = passportFromDetails(prefill, item.paper?.expiresOn); setPp(p); if (p.expiry) setExpires(p.expiry); } }, [prefill, item.paper?.expiresOn]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const pick = async (f: File | undefined) => {
    if (!f) return;
    setError(null); setPreparing(true); setFile(null); reader.reset();
    if (preview) { URL.revokeObjectURL(preview); setPreview(null); }
    try {
      const ready = await normaliseForUpload(f);
      setFile(ready);
      if (ready.type.startsWith('image/')) setPreview(URL.createObjectURL(ready));
      if (isPassport && reader.enabled && ready.type.startsWith('image/')) {
        // On the phone, in a worker; the form stays usable while it reads.
        void reader.read(ready).then((read) => { if (read) { setPp((cur) => ({ ...cur, ...read })); if (read.expiry) setExpires(read.expiry); } });
      }
      setTimeout(() => saveRef.current?.focus(), 0);
    } catch (e) {
      setError(e instanceof NormaliseError ? e.message : 'This file couldn’t be prepared. Choose a JPEG, PNG or PDF.');
    } finally { setPreparing(false); }
  };

  const save = async () => {
    if (!file) return;
    setError(null); setProgress(0);
    const expiry = isPassport ? pp.expiry || expires : expires;
    try {
      const v = await api.crewUpload(rid, item.id, file, {
        title: item.label,
        ...(itemExpires(item.type) && expiry ? { expires_on: expiry } : {}),
        ...(isPassport ? { details: passportPayload({ ...pp, expiry }) } : {}),
      }, setProgress);
      onDone(v);
    } catch (err) {
      setError(apiError(err, 'The upload didn’t go through. Try again.').message);
      setProgress(null);
    }
  };

  const busy = preparing || progress !== null;
  const pickerLabel = `flex min-h-[44px] flex-1 cursor-pointer items-center justify-center rounded-[10px] border border-[#2a4a86] px-4 text-[15px] font-semibold text-[#e8eef8] hover:bg-[#0f2856] focus-within:ring-2 focus-within:ring-[#5aa9ff] ${busy ? 'pointer-events-none opacity-50' : ''}`;
  const upd = (k: keyof PassportDetails) => (e: React.ChangeEvent<HTMLInputElement>) => setPp((cur) => ({ ...cur, [k]: e.target.value }));

  return (
    <div className="flex flex-col gap-4">
      <div className={`${C.card} relative flex min-h-[160px] flex-col items-center justify-center gap-2 overflow-hidden p-4 text-center`}>
        {preview ? <img src={preview} alt={`Your ${item.label.toLowerCase()} photo`} className="max-h-[260px] w-full rounded-lg object-contain" />
          : file ? <><span className="text-[17px] font-semibold text-white">{file.name}</span><span className={`text-sm ${C.muted}`}>PDF · {fmtSize(file.size)}</span></>
          : preparing ? <span role="status" className={`text-base ${C.soft}`}>Getting your photo ready…</span>
          : <span className={`text-base ${C.soft}`}>{itemHint(item.type)}</span>}
        {preview && <PassportReadBadge state={reader.state} />}
      </div>

      <div className="flex gap-2.5">
        <label className={pickerLabel}>
          <input type="file" accept="image/*" capture="environment" aria-label="Take a photo" className="sr-only" disabled={busy} onChange={(e) => { void pick(e.target.files?.[0]); e.target.value = ''; }} />
          {file ? 'Retake' : 'Take a photo'}
        </label>
        <label className={pickerLabel}>
          <input type="file" accept="image/*,application/pdf" aria-label="Choose a file" className="sr-only" disabled={busy} onChange={(e) => { void pick(e.target.files?.[0]); e.target.value = ''; }} />
          Choose a file
        </label>
      </div>

      {isPassport ? (
        <div className="flex flex-col gap-3">
          <span className={C.label}>{passportFieldsLabel(reader)}</span>
          <PassportReadNote state={reader.state} />
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Field id="pp-surname" label="Surname"><input id="pp-surname" className={C.input} value={pp.surname} onChange={upd('surname')} autoComplete="family-name" autoCapitalize="characters" /></Field></div>
            <div className="col-span-2"><Field id="pp-given" label="Given names"><input id="pp-given" className={C.input} value={pp.given_names} onChange={upd('given_names')} autoComplete="given-name" autoCapitalize="characters" /></Field></div>
            <Field id="pp-nat" label="Nationality"><input id="pp-nat" className={`${C.input} font-mono uppercase`} value={pp.nationality} onChange={upd('nationality')} maxLength={3} placeholder="GBR" autoCapitalize="characters" /></Field>
            <Field id="pp-num" label="Passport number"><input id="pp-num" className={`${C.input} font-mono`} value={pp.number} onChange={upd('number')} autoCapitalize="characters" autoComplete="off" /></Field>
            <Field id="pp-dob" label="Date of birth"><input id="pp-dob" type="date" className={C.input} value={pp.date_of_birth} onChange={upd('date_of_birth')} autoComplete="bday" /></Field>
            <Field id="pp-exp" label="Expires"><input id="pp-exp" type="date" className={C.input} value={pp.expiry} onChange={(e) => { upd('expiry')(e); setExpires(e.target.value); }} /></Field>
          </div>
          <p className={`m-0 text-sm leading-relaxed ${C.muted}`}>These go on the crew list for Customs. They’re stored encrypted, and only you and the skipper can see them.</p>
        </div>
      ) : itemExpires(item.type) ? (
        <Field id="item-exp" label="Expires (if it has a date)"><input id="item-exp" type="date" className={C.input} value={expires} onChange={(e) => setExpires(e.target.value)} /></Field>
      ) : null}

      <ValidityLine rid={rid} type={item.type} expiry={isPassport ? pp.expiry || expires : expires} />
      <ErrorNote>{error}</ErrorNote>
      {progress !== null && (
        <div className="flex flex-col gap-1.5">
          <div className="h-2 overflow-hidden rounded bg-[#0f2856]" role="progressbar" aria-label="Uploading" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}>
            <div className="h-2 bg-[#2563eb] transition-[width]" style={{ width: `${Math.round(progress * 100)}%` }} />
          </div>
          <span role="status" className={`text-sm ${C.muted}`}>{progress < 1 ? `Uploading… ${Math.round(progress * 100)}%` : 'Saving…'}</span>
        </div>
      )}
      <PrimaryButton ref={saveRef} onClick={() => void save()} disabled={!file} busy={busy}>{isPassport ? 'Save passport' : 'Save'}</PrimaryButton>
    </div>
  );
}

// ---------------- the emergency contact form ----------------

function ContactAnswer({ rid, item, onDone, prefill }: { rid: string; item: CrewItemView; onDone: (v: CrewRequestView) => void; prefill: Record<string, string | null> | null }) {
  const [f, setF] = useState({ name: '', relationship: '', phone: '', email: '' });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (prefill) setF({ name: prefill.name ?? '', relationship: prefill.relationship ?? '', phone: prefill.phone ?? '', email: prefill.email ?? '' }); }, [prefill]);

  const save = async () => {
    const e: Record<string, string> = {};
    if (!f.name.trim()) e.name = 'Add their name';
    if (!f.phone.trim()) e.phone = 'Add a phone number, with the country code';
    if (f.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) e.email = 'That email address doesn’t look right';
    setErrs(e);
    if (Object.keys(e).length) { document.getElementById(`ec-${Object.keys(e)[0]}`)?.focus(); return; }
    setBusy(true); setError(null);
    try {
      onDone(await api.crewForm(rid, item.id, { name: f.name.trim(), relationship: f.relationship.trim() || null, phone: f.phone.trim(), email: f.email.trim() || null }));
    } catch (err) { setError(apiError(err, 'The contact wasn’t saved. Try again.').message); setBusy(false); }
  };
  const input = (k: keyof typeof f, label: string, extra: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <Field id={`ec-${k}`} label={label} error={errs[k]}>
      <input id={`ec-${k}`} className={C.input} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} aria-invalid={errs[k] ? true : undefined} aria-describedby={errs[k] ? `ec-${k}-err` : undefined} {...extra} />
    </Field>
  );
  return (
    <form className="flex flex-col gap-3.5" onSubmit={(e) => { e.preventDefault(); void save(); }} noValidate>
      <p className={`m-0 text-base leading-relaxed ${C.soft}`}>Someone ashore the skipper can call if something happens at sea.</p>
      {input('name', 'Name', { autoComplete: 'off' })}
      {input('relationship', 'Relationship', { placeholder: 'e.g. partner, sister' })}
      {input('phone', 'Phone', { type: 'tel', inputMode: 'tel', placeholder: '+44 7700 900123', autoComplete: 'off' })}
      {input('email', 'Email (optional)', { type: 'email', inputMode: 'email', autoComplete: 'off' })}
      <ErrorNote>{error}</ErrorNote>
      <PrimaryButton type="submit" busy={busy}>Save emergency contact</PrimaryButton>
    </form>
  );
}

// ---------------- not needed ----------------

function NotNeededAnswer({ rid, item, onDone, onCancel }: { rid: string; item: CrewItemView; onDone: (v: CrewRequestView) => void; onCancel: () => void }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const save = async () => {
    if (!reason.trim()) { setError('Say briefly why it isn’t needed'); ref.current?.focus(); return; }
    setBusy(true); setError(null);
    try { onDone(await api.crewNotNeeded(rid, item.id, reason.trim())); } catch (err) { setError(apiError(err, 'That wasn’t saved. Try again.').message); setBusy(false); }
  };
  return (
    <form className={`${C.card} flex flex-col gap-3 p-4`} onSubmit={(e) => { e.preventDefault(); void save(); }}>
      {item.notNeededHint && <p className={`m-0 text-[15px] leading-relaxed ${C.soft}`}>{item.notNeededHint}</p>}
      <label htmlFor="nn-reason" className="text-sm font-semibold text-[#c7d3e6]">Why isn’t it needed?</label>
      <textarea id="nn-reason" ref={ref} rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. I’m a New Zealand citizen"
        className={`${C.input} min-h-[76px] py-2.5 leading-snug`} />
      <ErrorNote>{error}</ErrorNote>
      <div className="flex gap-2.5">
        <SecondaryButton className="flex-1" onClick={onCancel}>Cancel</SecondaryButton>
        <button type="submit" disabled={busy} className={`flex min-h-[44px] flex-1 items-center justify-center rounded-[10px] bg-[#2563eb] px-4 text-[15px] font-semibold text-white disabled:opacity-50 ${C.focus}`}>Save — not needed</button>
      </div>
    </form>
  );
}

// ---------------- reuse ----------------

function ReuseList({ rid, item, mine, onDone }: { rid: string; item: CrewItemView; mine: MyPaper[]; onDone: (v: CrewRequestView) => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const use = async (id: string) => {
    setBusy(id); setError(null);
    try { onDone(await api.crewReuse(rid, item.id, id)); } catch (err) { setError(apiError(err, 'That paper couldn’t be used. Try again.').message); setBusy(null); }
  };
  return (
    <div className="flex flex-col gap-2">
      {mine.map((d) => (
        <button key={d.id} type="button" onClick={() => void use(d.id)} disabled={!!busy}
          className={`flex min-h-[56px] items-center justify-between gap-3 rounded-xl border border-[#2a4a86] px-4 text-left text-base font-semibold text-white hover:bg-[#0f2856] disabled:opacity-50 ${C.focus}`}>
          <span className="min-w-0 truncate">{d.title}</span>
          <span className={`shrink-0 text-sm font-normal ${C.muted}`}>{busy === d.id ? 'Using…' : d.expiresOn ? `expires ${fmtDay(d.expiresOn)} ${d.expiresOn.slice(0, 4)}` : ''}</span>
        </button>
      ))}
      <ErrorNote>{error}</ErrorNote>
    </div>
  );
}

// ---------------- the page ----------------

export function CrewItemPage() {
  const { rid = '', iid = '' } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const q = useCrewRequest(rid);
  const setView = useSetCrewRequest(rid);
  const [mode, setMode] = useState<'answer' | 'not-needed' | 'reuse' | null>(params.get('answer') === 'not-needed' ? 'not-needed' : null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const item = q.data?.items.find((i) => i.id === iid) ?? null;
  const mineQ = useQuery({ queryKey: ['papers-me', 'documents'], queryFn: () => api.papersMyPapers(), enabled: !!item && !item.form, retry: false });
  const detailsQ = useQuery({
    queryKey: ['papers-me', 'details', item?.paper?.id],
    queryFn: () => api.papersMyDetails(item?.paper?.id as string),
    enabled: !!item?.paper?.hasDetails && (item.form || item.type === 'passport'), retry: false,
  });

  const reusable = useMemo(() => {
    if (!item) return [];
    const seen = new Set<string>();
    return (mineQ.data ?? [])
      .filter((d) => d.type === item.type && d.id !== item.paper?.id)
      .sort((a, b) => Number(a.grantOnly) - Number(b.grantOnly))
      .filter((d) => { const k = `${d.title}|${d.expiresOn ?? ''}`; if (seen.has(k)) return false; seen.add(k); return true; });
  }, [mineQ.data, item]);

  if (q.isLoading) return <CrewShell><Loading /></CrewShell>;
  if (q.error || !q.data || !item) {
    return (
      <CrewShell back={{ to: `/papers/requests/${rid}`, label: 'Back to the list' }}>
        <ErrorNote>{q.error ? apiError(q.error, 'This paper could not be loaded').message : 'This paper is not on the request any more.'}</ErrorNote>
      </CrewShell>
    );
  }

  const { request: req } = q.data;
  const who = firstName(req.requesterName);
  const writable = isWritable(req);
  const shared = isSharedNow(item);
  const answered = isAnswered(item);
  const done = (v: CrewRequestView) => { setView(v); navigate(afterAnswer(v, rid)); };
  const status = itemStatus(item, req);

  const undo = async () => {
    setBusy(true); setError(null);
    try { setView(await api.crewUndo(rid, item.id)); setMode(null); } catch (err) { setError(apiError(err, 'That couldn’t be undone. Try again.').message); } finally { setBusy(false); }
  };

  const showAnswer = writable && !shared && (!answered || mode === 'answer');
  const prefill = detailsQ.data ?? null;

  return (
    <CrewShell back={{ to: `/papers/requests/${rid}`, label: 'Back to the list' }} right={req.requesterName ? `For ${req.requesterName}` : undefined}>
      <div className="flex flex-col gap-1.5">
        <StepHeading focusKey={`${item.id}:${mode}`}>{item.label}</StepHeading>
        {!item.required && <span className={`text-sm ${C.muted}`}>Optional — only if you have one</span>}
      </div>

      {shared && (
        <div className={`${C.card} flex flex-col gap-2 p-4`}>
          <span className="text-base font-semibold text-[#6ee7a2]">Shared with {who} until {fmtDay(accessEndDate(item.grant!.expiresAt, req.timezone))}</span>
          <ItemQuestion rid={rid} item={item} />
          <span className={`text-[15px] leading-relaxed ${C.soft}`}>To replace it, withdraw {who}’s access first.</span>
          <Link to={`/papers/requests/${rid}/shared`} className={`min-h-[44px] self-start py-2 text-[15px] ${C.link}`}>Change the date or withdraw</Link>
        </div>
      )}

      {!shared && answered && mode !== 'answer' && (
        <div className={`${C.card} flex flex-col gap-3 p-4`}>
          <span className="text-base font-semibold text-white">{item.state === 'not_needed' ? status.text : item.paper ? `Ready to share · ${item.paper.title}` : status.text}</span>
          {item.paper?.expiresOn && <span className={`text-[15px] ${C.soft}`}>Expires {fmtDay(item.paper.expiresOn)} {item.paper.expiresOn.slice(0, 4)}</span>}
          {writable && (
            <div className="flex gap-2.5">
              {item.state !== 'not_needed' && <SecondaryButton className="flex-1" onClick={() => setMode('answer')}>Replace</SecondaryButton>}
              <SecondaryButton className="flex-1" onClick={() => void undo()} disabled={busy}>{item.state === 'not_needed' ? 'Undo' : 'Remove'}</SecondaryButton>
            </div>
          )}
          {writable && item.paper && <Link to={`/papers/requests/${rid}/share`} className={`min-h-[44px] self-start py-2 text-[15px] ${C.link}`}>Share with {who}</Link>}
        </div>
      )}
      <ErrorNote>{error}</ErrorNote>

      {showAnswer && mode === 'not-needed' && (
        <NotNeededAnswer rid={rid} item={item} onDone={done} onCancel={() => setMode(answered ? null : 'answer')} />
      )}

      {showAnswer && mode !== 'not-needed' && (
        <>
          {item.form ? <ContactAnswer rid={rid} item={item} onDone={done} prefill={prefill} />
            : mode === 'reuse' ? (
              <div className="flex flex-col gap-3">
                <span className={C.label}>From your papers</span>
                <ReuseList rid={rid} item={item} mine={reusable} onDone={done} />
                <SecondaryButton onClick={() => setMode(answered ? 'answer' : null)}>Upload a new one instead</SecondaryButton>
              </div>
            ) : <FileAnswer rid={rid} item={item} onDone={done} prefill={item.type === 'passport' ? prefill : null} />}

          {!item.form && mode !== 'reuse' && reusable.length > 0 && (
            <button type="button" onClick={() => setMode('reuse')} className={`min-h-[44px] py-1.5 text-center text-[15px] ${C.link} ${C.focus}`}>
              Use the {item.type === 'passport' ? 'passport' : 'one'} already in my papers
            </button>
          )}
          {item.notNeededAllowed && (
            <button type="button" onClick={() => setMode('not-needed')} className={`min-h-[44px] py-1.5 text-center text-[15px] ${C.link} ${C.focus}`}>Not needed</button>
          )}
        </>
      )}

      {!writable && <p className={`m-0 text-[15px] ${C.soft}`}>This request is closed, so nothing here can change.</p>}
    </CrewShell>
  );
}

export default CrewItemPage;
